import { msalConfig, defaultScopes } from "services/authProvider";
import { clearMfaValidated } from "utils/mfaStorage";
import {
  safeMsalLoginRedirect,
  setPendingLoginRedirectFlag,
  clearPendingLoginRedirectFlag,
} from "utils/msalLoginRedirect";

/**
 * Collect strings from MSAL ServerError / wrappers (shape varies by version).
 * @param {unknown} error
 * @param {number} depth
 * @param {string[]} out
 * @returns {string[]}
 */
export function flattenErrorStrings(error, depth = 0, out = []) {
  if (depth > 8 || error == null) return out;  if (typeof error === "string") {
    out.push(error);
    return out;
  }
  if (typeof error === "number" || typeof error === "boolean") {
    out.push(String(error));
    return out;
  }
  if (error instanceof Error) {
    out.push(error.message, error.name, error.stack || "");
  }
  if (typeof error === "object") {
    for (const k of Object.keys(error)) {
      flattenErrorStrings(error[k], depth + 1, out);
    }
  }
  return out;
}

/**
 * @param {unknown} [error]
 * @returns {string}
 */
export function collectRedirectErrorText(error) {
  const fromErr = flattenErrorStrings(error).join(" ");
  const fromUrl =
    typeof window !== "undefined"
      ? `${window.location.hash} ${window.location.search} ${window.location.href}`
      : "";
  const fromStorage =
    typeof window !== "undefined"
      ? localStorage.getItem("msal.login.error") || ""
      : "";
  return `${fromErr} ${fromUrl} ${fromStorage}`;
}

/** B2C SUSI "Forgot your password?" → access_denied / AADB2C90118 */
export function isB2cForgotPasswordRedirectError(error) {
  return collectRedirectErrorText(error).includes("AADB2C90118");
}

/** User cancelled a B2C journey (e.g. Cancel on password-reset policy) → AADB2C90091 */
export function isB2cUserCancelledError(error) {
  return collectRedirectErrorText(error).includes("AADB2C90091");
}

/** Clear MSAL/app auth keys but keep trusted-device id. */
export function clearB2cAuthStoragePreservingDevice() {
  const deviceId = localStorage.getItem("device_id");
  localStorage.clear();
  if (deviceId) {
    localStorage.setItem("device_id", deviceId);
  }
}

/**
 * Clear stale auth state and redirect to the default SUSI sign-in policy.
 * Deferred one tick so MSAL can finish tearing down a failed redirect interaction.
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {string[]} [scopes]
 */
export function scheduleB2cSignInRedirect(instance, scopes = defaultScopes) {
  setPendingLoginRedirectFlag();
  clearB2cAuthStoragePreservingDevice();
  window.setTimeout(() => {
    safeMsalLoginRedirect(instance, {
      scopes,
      authority: msalConfig.auth.authority,
    }, { skipPendingFlag: true }).catch(() => {});
  }, 0);
}
/**
 * MFA "Back to login": show loader, clear app auth/MFA state, then redirect to the
 * env-configured B2C custom policy (REACT_APP_AZURE_AD_AUTHORITY). Avoids a logout
 * round-trip that left `pendingLoginRedirect` set without a follow-up sign-in.
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {string[]} [scopes]
 */
export function redirectToB2cSignInFromMfa(instance, scopes = defaultScopes) {
  setPendingLoginRedirectFlag();
  clearMfaValidated();
  clearB2cAuthStoragePreservingDevice();

  const startSignIn = () => {
    safeMsalLoginRedirect(
      instance,
      {
        scopes,
        authority: msalConfig.auth.authority,
        prompt: "login",
      },
      { skipPendingFlag: true },
    ).catch((err) => {
      clearPendingLoginRedirectFlag();
      console.error("MFA back-to-login redirect failed:", err);
    });
  };

  const cacheClear = instance.clearCache?.();
  if (cacheClear && typeof cacheClear.then === "function") {
    cacheClear.finally(startSignIn);
  } else {
    startSignIn();
  }
}